import { Users, ArrowRight, Settings2, Sparkle, Send, CalendarClock, BadgeCheck, XCircle, Gem } from "lucide-react";
import { areaStyle } from "@/components/db/areaStyle";
import { STATUS_META, STATUS_ORDER } from "@/lib/pipeline";

export function AreaStrip({ areas, total, active, onPick, onShowAll, onManage }) {
  const filled = areas.filter((a) => a.count > 0 || a.area === active);
  return (
    <section className="mt-6" data-testid="area-strip">
      <h2 className="mb-3 text-base md:text-lg font-head font-semibold text-[#1e2a5a]">Prehľad všetkých kandidátov v databáze podľa oblasti</h2>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
        <AreaCard testId="area-card-all" Icon={Users} cls="text-violet-600 bg-violet-50" name="Všetky oblasti" count={total}
          active={active === "all"} onClick={() => onPick("all")} />
        {filled.map((a) => {
          const { Icon, cls } = areaStyle(a.area);
          return <AreaCard key={a.area} testId={`area-card-${a.area}`} Icon={Icon} cls={cls} name={a.area} count={a.count}
            active={active === a.area} onClick={() => onPick(a.area)} />;
        })}
      </div>
      <div className="mt-2 flex flex-wrap justify-end gap-x-4 gap-y-1 text-xs sm:text-sm">
        <button data-testid="manage-areas-btn" onClick={onManage} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-violet-700">
          <Settings2 size={15} /> Spravovať oblasti a pozície
        </button>
        <button data-testid="show-all-areas-btn" onClick={onShowAll} className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          Zobraziť všetky oblasti <ArrowRight size={15} />
        </button>
      </div>
    </section>
  );
}

const STATUS_ICONS = { new: Sparkle, in_process: Send, interview: CalendarClock, hired: BadgeCheck, rejected_shredded: XCircle, rejected_retain: Gem };
const STATUS_ICON_CLS = {
  new: "text-slate-600 bg-slate-100", in_process: "text-orange-600 bg-orange-50", interview: "text-purple-600 bg-purple-50",
  hired: "text-emerald-600 bg-emerald-50", rejected_shredded: "text-red-600 bg-red-50", rejected_retain: "text-teal-600 bg-teal-50",
};

export function StatusStrip({ counts, active, onPick, title = "Prehľad všetkých kandidátov podľa stavu", testId = "status-strip" }) {
  return (
    <section className="mt-8" data-testid={testId}>
      <h2 className="mb-3 text-base md:text-lg font-head font-semibold text-[#1e2a5a]">{title}</h2>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
        <AreaCard testId="status-card-all" Icon={Users} cls="text-violet-600 bg-violet-50" name="Všetci" count={counts.all || 0}
          active={active.length === 0} onClick={() => onPick([])} />
        {STATUS_ORDER.map((s) => (
          <AreaCard key={s} testId={`status-card-${s}`} Icon={STATUS_ICONS[s]} cls={STATUS_ICON_CLS[s]} name={STATUS_META[s].l}
            count={counts[s] || 0} active={active.length === 1 && active[0] === s} onClick={() => onPick([s])} accent={STATUS_META[s].solid} />
        ))}
      </div>
    </section>
  );
}

function AreaCard({ testId, Icon, cls, name, count, active, onClick, accent }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`relative overflow-hidden snap-start flex-shrink-0 w-40 rounded-2xl border bg-white p-4 text-left shadow-sm transition-[box-shadow,transform,border-color] hover:-translate-y-0.5 hover:shadow-md ${active ? "border-violet-400 ring-2 ring-violet-100" : "border-slate-200"}`}>
      {accent && <span className={`absolute inset-x-0 top-0 h-1 ${accent}`} />}
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${cls}`}><Icon size={18} /></span>
      <div className="mt-3 truncate text-sm font-semibold text-[#1e2a5a]">{name}</div>
      <div className="mt-1 font-head text-xl font-bold text-[#1e2a5a]" data-testid={`${testId}-count`}>{count}</div>
      <div className="text-[11px] text-slate-400">kandidátov</div>
    </button>
  );
}

export function PositionChips({ area, positions, active, onPick }) {
  const list = [...positions].sort((a, b) => b.count - a.count);
  const { Icon, cls } = areaStyle(area);
  return (
    <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="position-chips">
      <h2 className="flex items-center gap-2 text-base md:text-lg font-head font-semibold text-[#1e2a5a]">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-xl ${cls}`}><Icon size={16} /></span> Pracovné pozície v oblasti {area}
      </h2>
      {list.length === 0 && <p className="mt-3 text-sm text-slate-400">V tejto oblasti zatiaľ nie sú žiadne pozície.</p>}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-2">
        {list.map((p) => (
          <button key={p.name} data-testid={`position-chip-${p.name}`} onClick={() => onPick(active === p.name ? "" : p.name)}
            className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${active === p.name ? "bg-violet-600 border-violet-600 text-white" : p.count ? "bg-white border-slate-200 text-slate-800 hover:border-violet-300" : "bg-slate-50 border-slate-100 text-slate-400 hover:border-slate-300"}`}>
            <span className="truncate font-medium">{p.name}</span>
            <span className={`flex-shrink-0 rounded-full px-2 text-xs font-bold ${active === p.name ? "bg-white/25" : p.count ? "bg-violet-50 text-violet-700" : "bg-white text-slate-300"}`} data-testid={`position-count-${p.name}`}>{p.count}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
